import React, { useId, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import ItemCard from "./ItemCard";

const GRID = "grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5";
const PAGE_SIZE = 10;

// One named group of items on the ordering page's browse view (an AI Menu
// section, or an Inventory category), capped at PAGE_SIZE with an
// independent "View More" — spec: "pagination should work independently
// for each section or category."
//
// Two paging modes, picked by whether `onLoadMore` is passed:
//   - client mode (AI Menu sections): `items` already holds every item in
//     the section (small, already-loaded JSON blob) — "View More" just
//     reveals more of what's already here, no network call.
//   - server mode (Inventory categories): `items` holds only the first
//     page; `onLoadMore(offset)` fetches the next page from
//     GET /items/category-items and the caller appends it.
export default function ItemSection({
  title,
  icon: Icon,
  items,
  total,
  isFavourite,
  onToggleFavourite,
  onLoadMore,
}) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);
  // Expanded by default so the page looks exactly as it did before this
  // feature; each ItemSection instance is its own component, so this state
  // is already independent per section/category — no shared key needed.
  const [expanded, setExpanded] = useState(true);
  const panelId = useId();

  if (!items || items.length === 0) return null;

  const isServerPaged = typeof onLoadMore === "function";
  const shown = isServerPaged ? items : items.slice(0, visibleCount);
  const hasMore = isServerPaged ? items.length < (total ?? items.length) : visibleCount < items.length;

  const handleViewMore = async () => {
    if (isServerPaged) {
      setLoadingMore(true);
      try {
        await onLoadMore();
      } finally {
        setLoadingMore(false);
      }
    } else {
      setVisibleCount((n) => n + PAGE_SIZE);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        aria-controls={panelId}
        className="mb-3 flex w-full items-center justify-between gap-2 rounded-lg py-1 text-left -mx-1 px-1 hover:bg-gray-50"
      >
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          {Icon && <Icon className="h-4 w-4" />} {title}
        </h2>
        <span
          className="flex h-8 w-8 flex-none items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          aria-hidden="true"
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {expanded && (
        <div id={panelId}>
          <div className={GRID}>
            {shown.map((item) => {
              const isMenuItem = item.source === "ai_menu";
              // AI Menu items carry their own is_favourite flag (set by the
              // merchant on the AI Menu page, not toggleable here) — show
              // the heart from that, not from the shopper's isFavourite().
              const favourited = isMenuItem
                ? item.is_favourite === true
                : Boolean(isFavourite && isFavourite(item));
              return (
                <ItemCard
                  key={item.upc || item.id}
                  item={item}
                  isFavourite={favourited}
                  onToggleFavourite={isMenuItem ? undefined : onToggleFavourite}
                />
              );
            })}
          </div>
          {hasMore && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={handleViewMore}
                disabled={loadingMore}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
              >
                {loadingMore ? "Loading…" : "View more"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
